import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Bell, CheckCircle2, ChevronDown, Download, KeyRound, Loader2, MessageSquareShare, Settings2 } from "lucide-react"
import { useParams } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { apiRequest } from "@/lib/api"
import type { ApiError } from "@/lib/api"
import type { Project } from "@/types"

type SettingsPageProps = {
  currentProject: Project | null
}

type ProjectResponse = {
  project: Project
}

const throttleToInput = (value: number | null | undefined) => {
  if (value == null) return ""
  return String(value)
}

const SettingsPage = ({ currentProject }: SettingsPageProps) => {
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [formName, setFormName] = useState("")
  const [formThrottle, setFormThrottle] = useState("")
  const [formFacebookPageId, setFormFacebookPageId] = useState("")
  const [formFacebookAccessToken, setFormFacebookAccessToken] = useState("")
  const [isPushInstructionsOpen, setIsPushInstructionsOpen] = useState(false)

  const pushAssetsPath = `/api/projects/${encodeURIComponent(projectSlug || "")}/push`

  const { data, isLoading, isError, error } = useQuery<ProjectResponse, ApiError>({
    queryKey: ["project-settings", projectSlug],
    queryFn: () => apiRequest<ProjectResponse>(`/api/projects/${encodeURIComponent(projectSlug || "")}`),
    enabled: Boolean(projectSlug),
    refetchOnWindowFocus: false,
  })

  const project = data?.project ?? null

  useEffect(() => {
    if (!project) return
    setFormName(project.name)
    setFormThrottle(throttleToInput(project.message_throttle_per_minute))
    setFormFacebookPageId(project.facebook_page_id ?? "")
    setFormFacebookAccessToken("")
  }, [
    project?.id,
    project?.name,
    project?.message_throttle_per_minute,
    project?.facebook_page_id,
  ])

  const throttleError = useMemo(() => {
    const value = formThrottle.trim()
    if (!value) return null
    if (!/^\d+$/.test(value)) return "Use um número inteiro maior que zero ou deixe em branco."
    if (Number(value) <= 0) return "Use um número inteiro maior que zero ou deixe em branco."
    return null
  }, [formThrottle])

  const hasUnsavedChanges = useMemo(() => {
    if (!project) return false
    return (
      formName.trim() !== project.name ||
      (formThrottle.trim() || "") !== throttleToInput(project.message_throttle_per_minute) ||
      formFacebookPageId.trim() !== (project.facebook_page_id ?? "") ||
      formFacebookAccessToken.trim().length > 0
    )
  }, [formFacebookAccessToken, formFacebookPageId, formName, formThrottle, project])

  const updateMutation = useMutation<ProjectResponse, ApiError>({
    mutationFn: async () => {
      if (!projectSlug) {
        throw new Error("Projeto não encontrado.")
      }

      const payload: {
        name: string
        message_throttle_per_minute: number | null
        facebook_page_id: string | null
        facebook_page_access_token?: string
      } = {
        name: formName.trim(),
        message_throttle_per_minute: formThrottle.trim() ? Number(formThrottle.trim()) : null,
        facebook_page_id: formFacebookPageId.trim() || null,
      }

      if (formFacebookAccessToken.trim()) {
        payload.facebook_page_access_token = formFacebookAccessToken.trim()
      }

      return apiRequest<ProjectResponse>(`/api/projects/${encodeURIComponent(projectSlug)}`, {
        method: "PATCH",
        body: JSON.stringify({ project: payload }),
      })
    },
    onSuccess: (payload) => {
      queryClient.setQueryData(["project-settings", projectSlug], payload)
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["me"] })
      setFormFacebookAccessToken("")
      toast({
        title: "Configurações salvas",
        description: "As alterações do projeto foram atualizadas com sucesso.",
      })
    },
  })

  const handleReset = () => {
    if (!project) return
    setFormName(project.name)
    setFormThrottle(throttleToInput(project.message_throttle_per_minute))
    setFormFacebookPageId(project.facebook_page_id ?? "")
    setFormFacebookAccessToken("")
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!project) return
    if (!formName.trim()) return
    if (throttleError) return
    updateMutation.mutate()
  }

  const displayProjectName = project?.name ?? currentProject?.name ?? "Projeto"
  const messengerReady = project?.facebook_credentials_configured === true
  const accessTokenConfigured = project?.facebook_access_token_configured === true
  const pageIdConfigured = Boolean(project?.facebook_page_id)

  if (!projectSlug) {
    return (
      <div className="rounded-xl border border-dashed p-8 text-sm text-muted-foreground">
        Selecione um projeto para acessar as configurações.
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center rounded-xl border bg-card">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Carregando configurações...
        </div>
      </div>
    )
  }

  if (isError || !project) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-destructive">
        <div className="flex items-center gap-2 text-sm font-medium">
          <AlertCircle className="h-4 w-4" />
          Falha ao carregar as configurações do projeto
        </div>
        <p className="mt-2 text-sm text-destructive/90">
          {error?.message ?? "Tente novamente em instantes."}
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 pb-10">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configurações</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Atualize os dados do projeto e deixe o canal do Facebook Messenger pronto para envio.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={messengerReady ? "secondary" : "outline-solid"} className="px-3 py-1">
            {messengerReady ? "Messenger pronto" : "Messenger pendente"}
          </Badge>
        </div>
      </div>

      <Card className="border-primary/15 bg-linear-to-br from-primary/5 via-background to-background">
        <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-primary" />
              {displayProjectName}
            </CardTitle>
            <CardDescription className="font-mono text-xs">/{project.slug}</CardDescription>
          </div>
          <div className="grid gap-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className={`h-4 w-4 ${pageIdConfigured ? "text-emerald-600" : "text-muted-foreground"}`} />
              <span>{pageIdConfigured ? "Page ID informado" : "Page ID pendente"}</span>
            </div>
            <div className="flex items-center gap-2">
              <KeyRound className={`h-4 w-4 ${accessTokenConfigured ? "text-emerald-600" : "text-muted-foreground"}`} />
              <span>{accessTokenConfigured ? "Token salvo com segurança" : "Token ainda não informado"}</span>
            </div>
          </div>
        </CardHeader>
      </Card>

      <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Projeto</CardTitle>
            <CardDescription>
              Defina como este projeto aparece no painel e ajuste o limite de envios por minuto.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-5 sm:grid-cols-2">
            <div className="grid gap-2 sm:col-span-2">
              <Label htmlFor="project-name">Nome do projeto</Label>
              <Input
                id="project-name"
                value={formName}
                onChange={(event) => setFormName(event.target.value)}
                placeholder="ex.: Atendimento, Marketplace, Growth"
                disabled={updateMutation.isPending}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="project-slug">Slug</Label>
              <Input id="project-slug" value={project.slug} disabled />
              <p className="text-xs text-muted-foreground">
                O slug identifica o projeto na URL atual.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="project-throttle">Limite de taxa (msgs/min)</Label>
              <Input
                id="project-throttle"
                type="number"
                min={1}
                inputMode="numeric"
                value={formThrottle}
                onChange={(event) => setFormThrottle(event.target.value)}
                placeholder="Deixe em branco para sem limite"
                disabled={updateMutation.isPending}
              />
              <p className="text-xs text-muted-foreground">
                Use um valor para reduzir o ritmo de disparos ou deixe vazio para sem limite.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquareShare className="h-5 w-5 text-primary" />
              Facebook Messenger
            </CardTitle>
            <CardDescription>
              Informe o Page ID e o token da página para habilitar os envios do Messenger neste projeto.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-5">
            <div className="grid gap-2">
              <Label htmlFor="facebook-page-id">Facebook Page ID</Label>
              <Input
                id="facebook-page-id"
                value={formFacebookPageId}
                onChange={(event) => setFormFacebookPageId(event.target.value)}
                placeholder="123456789012345"
                disabled={updateMutation.isPending}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="facebook-page-token">Page Access Token</Label>
              <Input
                id="facebook-page-token"
                type="password"
                value={formFacebookAccessToken}
                onChange={(event) => setFormFacebookAccessToken(event.target.value)}
                placeholder={
                  accessTokenConfigured
                    ? "Já existe um token salvo. Preencha apenas para substituir."
                    : "EAAB..."
                }
                disabled={updateMutation.isPending}
              />
              <p className="text-xs text-muted-foreground">
                {accessTokenConfigured
                  ? "O token atual permanece salvo de forma criptografada se este campo ficar em branco."
                  : "O token é salvo de forma criptografada e não volta para o frontend após o salvamento."}
              </p>
            </div>
            <div className="rounded-lg border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
              O Messenger só fica pronto para envio quando os dois campos estiverem preenchidos: o <strong>Page ID</strong> e o <strong>Page Access Token</strong>.
            </div>
          </CardContent>
          <CardFooter className="flex flex-col items-stretch gap-3 border-t pt-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              {throttleError ? (
                <p className="text-sm text-destructive">{throttleError}</p>
              ) : updateMutation.isError ? (
                <p className="text-sm text-destructive">
                  {updateMutation.error.message}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Salve as alterações para atualizar as configurações do projeto.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={updateMutation.isPending || !hasUnsavedChanges}
              >
                Restaurar
              </Button>
              <Button
                type="submit"
                disabled={
                  updateMutation.isPending ||
                  !formName.trim() ||
                  Boolean(throttleError) ||
                  !hasUnsavedChanges
                }
              >
                {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar configurações
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <div>
              <CardTitle>Arquivos de Configuração Push</CardTitle>
              <CardDescription>
                Baixe o service worker do Firebase e o helper de inscrição para este projeto.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={`${pushAssetsPath}/sw.js`}>
                <Download className="mr-2 h-4 w-4" />
                Download sw.js
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={`${pushAssetsPath}/subscribe.js`}>
                <Download className="mr-2 h-4 w-4" />
                Download subscribe.js
              </a>
            </Button>
          </div>

          <Collapsible
            open={isPushInstructionsOpen}
            onOpenChange={setIsPushInstructionsOpen}
            className="mt-4"
          >
            <div className="rounded-md border bg-muted/20 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">Como usar estes arquivos</p>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs">
                    {isPushInstructionsOpen ? "Ocultar instruções" : "Mostrar instruções"}
                    <ChevronDown
                      className={`ml-1 h-4 w-4 transition-transform ${isPushInstructionsOpen ? "rotate-180" : ""}`}
                    />
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>

            <CollapsibleContent className="mt-3 rounded-md border bg-muted/20 p-4">
              <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                <li>Baixe os dois arquivos nesta página.</li>
                <li>Envie o <code className="font-mono text-foreground">sw.js</code> para a raiz do seu site para que seja servido em <code className="font-mono text-foreground">/sw.js</code>.</li>
                <li>Envie o <code className="font-mono text-foreground">subscribe.js</code> e inclua-o nas páginas onde os usuários podem optar por receber push.</li>
                <li>Escolha o prompt automático no carregamento via <code className="font-mono text-foreground">window.BroadcastPushConfig</code> ou o prompt manual via <code className="font-mono text-foreground">window.BroadcastPush.subscribe(...)</code>.</li>
              </ol>

              <pre className="mt-3 overflow-x-auto rounded-md border bg-background p-3 text-xs text-foreground">
                {`<!-- Option A: auto-prompt on page load -->
<script>
  function getCountryFromBrowser() {
    try {
      if (typeof Intl !== "undefined" && typeof Intl.Locale !== "undefined") {
        const locale = new Intl.Locale(navigator.language);
        if (locale.region) return locale.region.toUpperCase();
      }
      const languages = navigator.languages?.length
        ? navigator.languages
        : [navigator.language].filter(Boolean);
      for (const lang of languages) {
        const match = lang.match(/[-_]([A-Za-z]{2})$/);
        if (match) return match[1].toUpperCase();
      }
    } catch (_error) {}
    return "US";
  }

  window.BroadcastPushConfig = {
    autoPromptOnLoad: true,
    autoPromptDelayMs: 500,
    serviceWorkerPath: "/sw.js",
    externalUserId: "user-123",
    country: getCountryFromBrowser(),
    subscribedUrl: window.location.origin + window.location.pathname,
    metadata: { source: "website" }
  };
</script>
<script src="/subscribe.js"></script>

<!-- Option B: manual prompt on button click -->
<script src="/subscribe.js"></script>
<button id="enable-push">Ativar notificações</button>
<script>
  function getCountryFromBrowser() {
    try {
      if (typeof Intl !== "undefined" && typeof Intl.Locale !== "undefined") {
        const locale = new Intl.Locale(navigator.language);
        if (locale.region) return locale.region.toUpperCase();
      }
      const languages = navigator.languages?.length
        ? navigator.languages
        : [navigator.language].filter(Boolean);
      for (const lang of languages) {
        const match = lang.match(/[-_]([A-Za-z]{2})$/);
        if (match) return match[1].toUpperCase();
      }
    } catch (_error) {}
    return "US";
  }

  document.getElementById("enable-push").addEventListener("click", async () => {
    await window.BroadcastPush.subscribe({
      serviceWorkerPath: "/sw.js",
      externalUserId: "user-123",
      country: getCountryFromBrowser(),
      subscribedUrl: window.location.origin + window.location.pathname,
      metadata: { source: "website" }
    });
  });
</script>`}
              </pre>

              <p className="mt-2 text-xs text-muted-foreground">
                Alguns navegadores podem suprimir prompts sem gesto do usuário. Notificações push exigem HTTPS em produção (localhost é permitido para desenvolvimento local).
              </p>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>
    </div>
  )
}

export default SettingsPage
